import * as Sentry from "@sentry/nextjs";
import { AxiosError } from "axios";
import { makeGetUrl } from "./utils";
import ServiceApi from "./api";

const BaseModel = class {
  protected _serviceApi: ServiceApi;
  protected _errorCallbackFunc: (error: any) => void;
  protected _successMessage: string;

  constructor(accessToken?: string | null) {
    this._serviceApi = new ServiceApi();
    this._errorCallbackFunc = (error) => {};
    this._successMessage = "success";
    this._serviceApi.handlerBaseUrl = (process.env.API_URL || process.env.NEXT_PUBLIC_API_URL) as string;
    if (accessToken) {
      this._serviceApi.setAuthorizationToken = accessToken;
    }
  }

  set setAccessToken(token: string) {
    this._serviceApi.setAuthorizationToken = token;
  }

  cleanAuthorization() {
    this._serviceApi.handler.cleanAuthorization();
  }

  set errorCallback(callback: (error: any) => void) {
    this._errorCallbackFunc = callback;
  }
  get errorCallback() {
    return this._errorCallbackFunc;
  }

  makeGetUrl(url: string, params?: any) {
    return makeGetUrl(url, params || {});
  }

  async request(requestFunc: Function) {
    try {
      const apiResult = await requestFunc();
      return apiResult.data;
    } catch (error: any) {
      const isAxiosError = (candidate: any): candidate is AxiosError => (candidate.isAxiosError = true);
      Sentry.captureMessage("Api error");
      Sentry.captureMessage(`is axios error :: ${isAxiosError(error)}`);
      Sentry.captureException(error);
      this._errorCallbackFunc(error);
      return {
        result: false,
        error: error.response?.data || {},
        message: error.response?.data?.message || error.message,
        status: error.response?.status,
      };
    }
  }

  async get(url: string, cache: boolean = false, options: Record<string, any> = {}): Promise<any> {
    const optionsWithCache = { ...options, cache };
    return await this.request(() => this._serviceApi.handler.get(url, optionsWithCache));
  }

  async post(url: string, data: any, options: Record<string, any> = {}): Promise<any> {
    return await this.request(() => this._serviceApi.handler.post(url, data, options));
  }

  async fileUpload(url: string, data: any): Promise<any> {
    return await this.request(() =>
      this._serviceApi.handler.post(url, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
    );
  }

  async put(url: string, data: any, options: Record<string, any> = {}): Promise<any> {
    return await this.request(() => this._serviceApi.handler.put(url, data, options));
  }

  async delete(url: string, options: Record<string, any> = {}): Promise<any> {
    return await this.request(() => this._serviceApi.handler.delete(url, options));
  }
};

export default BaseModel;
